import { ref, onMounted } from 'vue'
import type { User } from '@supabase/supabase-js'
import { Capacitor } from '@capacitor/core'
import { useSupabaseClient } from './useSupabaseClient'
import { useConvexMutation } from 'convex-vue'
import { api } from '../../convex/_generated/api'
import { markLoginIntent, clearLoginIntent } from './useSingleDeviceSession'
import { SocialLogin, type GoogleLoginResponseOnline, type AppleProviderResponse } from '@capgo/capacitor-social-login';

SocialLogin.initialize({
  google: {
    iOSClientId: '649947344012-tfv7qanjojcr64g3aqdbc1f4fa7b77mh.apps.googleusercontent.com',
    webClientId: '649947344012-t0f1ed5cqd5uelu5icdm94dt9dt0sseh.apps.googleusercontent.com',
  },
  apple: {
    clientId: 'com.kcfdaniel.drumblebee2',
  },
});

const user = ref<User>()
const { supabase } = useSupabaseClient();

export async function getCurrentUser() {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.user
}

// Mark that an explicit sign-in is underway so the single-device watcher claims
// this device once auth is ready (and doesn't kick us mid-login). Clears the
// mark if the sign-in throws.
async function withLoginClaim<T>(fn: () => Promise<T>): Promise<T> {
  markLoginIntent();
  try {
    return await fn();
  } catch (err) {
    clearLoginIntent();
    throw err;
  }
}

export function useSupabaseAuth() {
  // Convex mutation for deleting all user data
  const { mutate: deleteAllUserData } = useConvexMutation(api.users.deleteAllUserData);

  onMounted(async () => {
    // Get initial session
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    user.value = data.session?.user;

    // Listen for auth changes
    supabase.auth.onAuthStateChange((_, session) => {
      user.value = session?.user;
    })
  })

  const signInWithApple = () => withLoginClaim(async () => {
    if (Capacitor.getPlatform() !== 'ios') {
      throw new Error('Sign in with Apple is only supported on native iOS');
    }
    const res = await SocialLogin.login({
      provider: 'apple',
      options: {
        scopes: ['name', 'email'],
      },
    });
    const result = res.result as AppleProviderResponse;
    if (!result.idToken) throw new Error('Apple sign in failed: no id token');

    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: 'apple',
      token: result.idToken,
    });
    if (error) throw error;

    // Apple only provides name on first sign-in — save it if present
    if (data.user && (result.profile.givenName || result.profile.familyName)) {
      const fullName = [result.profile.givenName, result.profile.familyName]
        .filter(Boolean).join(' ');
      await supabase.auth.updateUser({
        data: {
          full_name: fullName,
          given_name: result.profile.givenName,
          family_name: result.profile.familyName,
        },
      });
    }
  });

  const signInWithGoogle = () => withLoginClaim(async () => {
    const res = await SocialLogin.login({
      provider: 'google',
      options: {
        scopes: ['email', 'profile'],
      },
    });
    const result = res.result as GoogleLoginResponseOnline;
    const { error } = await supabase.auth.signInWithIdToken({
      provider: 'google',
      token: result.idToken ?? "",
    })
    // const { error } = await supabase.auth.signInWithOAuth({
    //   provider: 'google',
    //   options: {
    //     queryParams: {
    //       access_type: 'offline',
    //       prompt: 'consent',
    //     },
    //     redirectTo: `${window.location.origin}/`
    //   }
    // })

    if (error) throw error
  })

  const signInWithOtp = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
    })
    if (error) throw error
  }

  const verifyOtp = (email: string, token: string) => withLoginClaim(async () => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })
    if (error) throw error
    return data
  })

  const signOut = async () => {
    await SocialLogin.logout({ provider: 'google' });
    const { error } = await supabase.auth.signOut();
    if (error) throw error
  }

  const deleteAccount = async () => {
    // 1. Delete all Convex data (items, songs, files)
    await deleteAllUserData({});

    // 2. Delete Supabase auth user (existing RPC)
    const { error } = await supabase.rpc('delete_user')
    if (error) throw error

    await signOut()
  }

  return {
    user,
    getCurrentUser,
    signInWithApple,
    signInWithGoogle,
    signInWithOtp,
    verifyOtp,
    signOut,
    deleteAccount
  }
}
